import { useState, useCallback } from 'react';
import { apiFetch } from '../../../auth/apiFetch';

/**
 * Hook for DOCX upload functionality.
 * Handles metadata lookup and file upload to S3.
 */
function useDocxUpload(apiBaseUrl) {
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  /**
   * Lookup metadata by filename.
   * Sends only the filename to the backend to find matching metadata.
   */
  const lookupMetadata = useCallback(async (filename) => {
    setIsLookingUp(true);
    try {
      const response = await apiFetch(`${apiBaseUrl}/api/docx/metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ filename }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || `HTTP ${response.status}`);
      }

      return await response.json();
    } finally {
      setIsLookingUp(false);
    }
  }, [apiBaseUrl]);

  /**
   * Upload DOCX file to S3 and create the Kimai project via a three-step flow.
   *
   * Step 1: POST /api/docx/upload-init (Lambda) — validates EAN, returns pre-signed S3 URL.
   * Step 2: PUT file directly to S3 — bypasses API Gateway payload limit.
   * Step 3: POST /api/docx/kimai-init (ECS) — creates Kimai project.
   *         Requires ECS to be running; has internet access unlike Lambda.
   */
  const uploadFile = useCallback(async (file, confirmedEan, digitalAssetCollectionKey) => {
    setIsUploading(true);
    try {
      // Step 1: request a pre-signed upload URL
      const initResponse = await apiFetch(`${apiBaseUrl}/api/docx/upload-init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          confirmed_ean: confirmedEan,
          ...(digitalAssetCollectionKey ? { digital_asset_collection_key: digitalAssetCollectionKey } : {}),
        }),
      });

      if (!initResponse.ok) {
        const error = await initResponse.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(error.detail || `HTTP ${initResponse.status}`);
      }

      const initData = await initResponse.json();

      // Step 2: upload the file directly to S3
      const s3Response = await fetch(initData.presigned_url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
        body: file,
      });

      if (!s3Response.ok) {
        throw new Error(`S3 upload failed: HTTP ${s3Response.status}`);
      }

      // Step 3: create Kimai project via ECS (has internet access)
      const kimaiResponse = await apiFetch(`${apiBaseUrl}/api/docx/kimai-init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ean: confirmedEan,
          ...(digitalAssetCollectionKey ? { digital_asset_collection_key: digitalAssetCollectionKey } : {}),
        }),
      });

      if (!kimaiResponse.ok) {
        const error = await kimaiResponse.json().catch(() => ({ detail: 'Unknown error' }));
        throw new Error(`Kimai project creation failed: ${error.detail || `HTTP ${kimaiResponse.status}`}`);
      }

      const kimaiData = await kimaiResponse.json();
      return { ...initData, ...kimaiData };
    } finally {
      setIsUploading(false);
    }
  }, [apiBaseUrl]);

  return {
    lookupMetadata,
    uploadFile,
    isLookingUp,
    isUploading,
  };
}

export default useDocxUpload;
